import { Globe, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { languages, type LanguageCode } from '../i18n';

interface LanguageSwitcherProps {
  variant?: 'default' | 'minimal';
  className?: string;
}

export function LanguageSwitcher({ variant = 'default', className = '' }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (code: LanguageCode) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  if (variant === 'minimal') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        {/* Topbar V2 (Aurora DS.html 139): czysty .iconbtn z kodem języka —
            bez flagi i strzałki. */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="iconbtn"
          aria-label={currentLang.name}
        >
          <span className="text-[12px] font-semibold tracking-wide">
            {currentLang.code.toUpperCase()}
          </span>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1 bg-aurora-raised rounded-ids-md shadow-elev-3 border border-aurora-border-strong py-1 z-50 min-w-[120px]">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-aurora-surface transition-colors ${
                  lang.code === currentLang.code
                    ? 'bg-aurora-surface text-aurora-text'
                    : 'text-aurora-muted'
                }`}
              >
                <span>{lang.flag}</span>
                <span className="text-sm">{lang.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-aurora-surface hover:border-aurora-border-strong border border-aurora-border rounded-ids-md text-aurora-muted hover:text-aurora-text transition-colors"
      >
        <Globe className="w-4 h-4" />
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm">{currentLang.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 bg-aurora-raised rounded-ids-md shadow-elev-3 border border-aurora-border-strong py-1 z-50 min-w-[150px]">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-aurora-surface transition-colors ${
                lang.code === currentLang.code
                  ? 'bg-aurora-surface text-aurora-text'
                  : 'text-aurora-muted'
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm font-medium">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
